import React, { ChangeEvent, FocusEvent, useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'

import { Input } from '@frame/ui-primitives/input'

import { useRootContext } from '@frame/lib/context'
import { useDebounce } from '@frame/lib/hooks/useDebounce'
import { DataAttribute } from '@frame/types'
import { Listbox, ListboxOptions } from '@headlessui/react'

import { Results } from './results'
import { AddressInfo } from './types'
import { useAddress } from './useAddress'

type Props = {
  name: string
  value?: AddressInfo
  label: string
  noAddress: boolean
  errors?: string
  disabled?: boolean
  required?: boolean
  onChange: (value: AddressInfo) => void
  clearErrors: (key: string) => void
}

export function Address({ name, value, label, noAddress, errors, disabled, required, clearErrors, onChange }: Props) {
  const { api } = useRootContext()
  const [selected, setSelected] = useState<AddressInfo | null>(null)
  const [open, setOpen] = useState(false)
  const listboxRef = useRef<HTMLDivElement | null>(null)

  const { query, setQuery } = useAddress({ selected, noAddress, onChange })
  const debouncedQuery = useDebounce(query, 500)
  const { isValidating, isLoading, data } = useSWR(['address', 'autocomplete', debouncedQuery], async () => {
    const { data, ...rest } = await api.common.getAddressAutocompletion(debouncedQuery)

    if (!data) {
      return { ...rest, data: [] }
    }
    return {
      ...rest,
      data: data.filter((datum) => Boolean(datum.place_id)),
    }
  })

  const results = useMemo(() => data?.data || [], [data])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    function handleClickOutside(event: MouseEvent) {
      if (listboxRef.current && !listboxRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
  }, [])

  useEffect(() => {
    if (value && !value?.description !== undefined) {
      setQuery(value?.description)
    }

    if (!query && value?.place_id) {
      const address: AddressInfo | undefined = results?.find((address) => address.place_id === value.place_id)

      if (address) {
        onChange(address)
      }
    }
  }, [value, noAddress, query, results, setQuery, onChange])

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const hasAddress = !noAddress

    if (hasAddress) {
      onChange({ description: e.currentTarget.value, place_id: '' })
      setQuery(e.currentTarget.value)
    }
    setOpen(Boolean(e.target.value))
  }

  function handleInputFocus(e: FocusEvent<HTMLInputElement>) {
    if (e.target.value) {
      setOpen(true)
    }
  }

  function handleChangeAddress(address: AddressInfo) {
    onChange(address)
    setSelected(address)
    clearErrors('address')
    setOpen(false)
  }

  const showLoader = isLoading || isValidating || debouncedQuery !== query
  const isDisabled = disabled || noAddress

  return (
    <div ref={listboxRef} className="relative">
      <Listbox name={name} value={selected} onChange={handleChangeAddress}>
        <Input
          data-cy={DataAttribute.ADDRESS_INPUT}
          name={name}
          label={label}
          value={value?.description || query}
          error={errors}
          required={required}
          disabled={isDisabled}
          placeholder="e.g. 123 Main St."
          type="text"
          onChange={handleInputChange}
          onFocus={handleInputFocus}
        />
        <ListboxOptions static>{open && <Results isLoading={showLoader} results={results} />}</ListboxOptions>
      </Listbox>
    </div>
  )
}
